import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import { PiBuildingsLight, PiPlusLight, PiUsersLight, PiPaperclipLight, PiChatCircleTextLight, PiTrashLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import ResourceTrash from '../../RecycleBin/ResourceTrash';
import Header from '../../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import Badge from '../../shared/TailwindComponents/Badge';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import Button from '../../shared/TailwindComponents/Button';
import { fetchAllDepartments, deleteDepartment } from '../../../store/actions/organization/DepartmentActions';
import DepartmentForm from './DepartmentForm';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { Tooltip } from '@mui/material';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, departmentListTourSteps } from '../../shared/FeatureTour';

import ActivityDrawer from '../../shared/activity/ActivityDrawer';
import Skeleton from '../../shared/TailwindComponents/Skeleton';

interface DepartmentRow {
  id: string;
  Name: React.ReactNode;
  'Practice Areas': React.ReactNode;
  'Members': React.ReactNode;
  'Documents': React.ReactNode;
  'Feed': React.ReactNode;
  Description: string;
  Status: React.ReactNode;
  Actions?: React.ReactNode;
}

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  document_count: number;
  feed_item_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    profile_picture?: string | null;
  }>;
  created_at?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
}

const DepartmentGridItem: React.FC<{ department: any; onClick: (id: string) => void }> = ({ department, onClick }) => {
  const { t } = useTranslation();
  const handleClick = () => {
    onClick(department.id);
  };

  return (
    <div
      className="glass relative cursor-pointer rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-md text-gray-900 dark:text-white font-semibold truncate">{department.name}</h3>
        {department.is_active !== undefined && (
          <Badge color={department.is_active ? 'green' : 'gray'}>
            {department.is_active ? t('Active') : t('Inactive')}
          </Badge>
        )}
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
        {department.description || t('No description')}
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <PiPaperclipLight className="w-4 h-4" />
          <span>{department.document_count || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <PiChatCircleTextLight className="w-4 h-4" />
          <span>{department.feed_item_count || 0}</span>
        </div>
      </div>

      {department.members && department.members.length > 0 && (
        <CollaboratorStack collaborators={department.members} max={3} size="md" withAccessLabels={false} />
      )}
    </div>
  );
};

const DepartmentList: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [isAddingDepartment, setIsAddingDepartment] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{ id: string; name: string } | null>(null);
  const itemsPerPage = 50;
  const [displayCount, setDisplayCount] = useState(itemsPerPage);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'department-list',
    steps: departmentListTourSteps,
    readyToShow: !loading && !isAddingDepartment && !showTrash,
    autoStartDelay: 600,
  });

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      setIsAddingDepartment(true);
    } else {
      setIsAddingDepartment(false);
    }
  }, [location]);

  useEffect(() => {
    let isMounted = true;

    const loadDepartments = async () => {
      setLoading(true);
      setError('');
      setIsFetching(true);
      try {
        const result = await dispatch(fetchAllDepartments() as any);
        if (isMounted && Array.isArray(result)) {
          setDepartments(result);
        } else if (isMounted) {
          setDepartments([]);
          setError(t('Failed to load departments'));
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Error loading departments:', err);
          setError(t('Failed to load departments'));
          setDepartments([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsFetching(false);
        }
      }
    };

    loadDepartments();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredDepartments = useMemo(() => {
    if (!Array.isArray(departments)) {
      return [];
    }
    if (!searchTerm.trim()) {
      return departments;
    }

    const lowerQuery = searchTerm.toLowerCase();
    return departments.filter(
      (dept) =>
        dept.name?.toLowerCase().includes(lowerQuery) ||
        dept.description?.toLowerCase().includes(lowerQuery)
    );
  }, [departments, searchTerm]);

  // Client-side pagination: show first N items
  const displayedDepartments = useMemo(() => {
    return filteredDepartments.slice(0, displayCount);
  }, [filteredDepartments, displayCount]);

  const hasMore = displayCount < filteredDepartments.length;

  const loadMore = useCallback(() => {
    setDisplayCount(prev => prev + itemsPerPage);
  }, []);

  // Reset display count when search or filters change
  useEffect(() => {
    setDisplayCount(itemsPerPage);
  }, [searchTerm, selectedFilters]);

  const handleCloseAddDepartment = async () => {
    setIsAddingDepartment(false);
    history.push('/departments');
    setLoading(true);
    setError('');
    setIsFetching(true);
    try {
      const result = await dispatch(fetchAllDepartments() as any);
      if (Array.isArray(result)) {
        setDepartments(result);
      } else {
        setDepartments([]);
        setError(t('Failed to load departments'));
      }
    } catch (err: any) {
      console.error('Error loading departments:', err);
      setError(t('Failed to load departments'));
      setDepartments([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const columns = ['Name', 'Practice Areas', 'Members', 'Documents', 'Feed', 'Description', 'Status', 'Actions'];

  const departmentTableActions = [
    {
      label: t('Activity Log'),
      onClick: (row: any) => {
        const dept = departments.find((d) => d.id === row.id);
        const name = dept?.name || '';
        setAuditTarget({ id: row.id, name });
        setShowAuditLog(true);
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Department'),
      onClick: (row: any) => {
        dispatch(showModal({
          type: 'confirmation',
          message: t('Delete Department'),
          subMsg: t('This department and all associated data will be permanently deleted. This action cannot be undone.'),
          icon: <PiTrashLight className='w-6 h-6 text-danger-500' />,
          isDest: true,
          showModal: true,
          onConfirm: async () => {
            await dispatch(deleteDepartment(row.id) as any);
            setLoading(true);
            setError('');
            setIsFetching(true);
            try {
              const updated = await dispatch(fetchAllDepartments() as any);
              if (Array.isArray(updated)) {
                setDepartments(updated);
              }
            } catch (err: any) {
              console.error('Error loading departments:', err);
              setError(t('Failed to load departments'));
            } finally {
              setLoading(false);
              setIsFetching(false);
              dispatch(hideModal());
            }
          },
          onCancel: () => dispatch(hideModal()),
        }));
      },
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const data = useMemo<DepartmentRow[]>(() => {
    if (!Array.isArray(displayedDepartments)) {
      return [];
    }
    return displayedDepartments.map((dept): DepartmentRow => {
      return {
        id: dept.id,
        Name: (
          <div className="flex items-center space-x-3">
            <div>
              <span className={!dept.is_active ? 'text-gray-500 dark:text-gray-400' : ''}>{dept.name}</span>
            </div>
          </div>
        ),
        'Practice Areas': dept.practice_areas && dept.practice_areas.length > 0 ? (
          <div className="flex flex-wrap gap-1 items-center">
            {dept.practice_areas.slice(0, 2).map((pa) => (
              <Badge key={pa.id} color="blue">
                {pa.name}
              </Badge>
            ))}
            {dept.practice_areas.length > 2 && (
              <Tooltip
                title={dept.practice_areas.slice(2).map((pa) => pa.name).join(', ')}
                arrow
                placement="top"
              >
                <span className="text-xs text-gray-500 dark:text-gray-400 cursor-default font-medium">
                  {t('+{{count}} more', { count: dept.practice_areas.length - 2 })}
                </span>
              </Tooltip>
            )}
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-sm">—</span>
        ),
        'Members': dept.members && dept.members.length > 0 ? (
          <CollaboratorStack collaborators={dept.members} max={4} withAccessLabels={false} />
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-sm">—</span>
        ),
        'Documents': (
          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <PiPaperclipLight className="w-4 h-4" />
            <span>{dept.document_count || 0}</span>
          </div>
        ),
        'Feed': (
          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <PiChatCircleTextLight className="w-4 h-4" />
            <span>{dept.feed_item_count || 0}</span>
          </div>
        ),
        Description: dept.description || '-',
        Status: (
          <Badge color={dept.is_active ? 'green' : 'gray'}>
            {dept.is_active ? t('Active') : t('Inactive')}
          </Badge>
        ),
      };
    });
  }, [displayedDepartments, t]);

  if (isAddingDepartment) {
    return (
      <div className='h-full'>
        <DepartmentForm onSuccess={handleCloseAddDepartment} onCancel={handleCloseAddDepartment} />
      </div>
    );
  }

  return (
    <>
      <div data-tour-id="dept-list-header">
      <Header
        actions={[
          {
            label: t('Add Department'),
            icon: <PiPlusLight className='w-4 h-4' />,
            onClick: () => history.push('/departments?add=true'),
            requiresSubscription: false,
          },
        ]}
      />
      </div>
      <div data-tour-id="dept-list-filters">
      <FilterBarWithTabs
        itemCount={displayedDepartments.length}
        totalItemCount={filteredDepartments.length}
        itemCountLabel="departments"
        filters={[]}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onClearAllFilters={() => setSelectedFilters({})}
        viewMode={viewMode}
        setViewMode={setViewMode}
        actions={showTrash ? trashBulkActions : []}
        selectionCount={showTrash ? trashSelectedCount : undefined}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Departments')}
        onRecycleBinToggle={(active) => {
          if (!active) {
            dispatch(fetchAllDepartments() as any).then((result: any) => {
              if (Array.isArray(result)) setDepartments(result);
            });
          }
          setShowTrash(active);
        }}
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          entityType="department"
          entityLabel="departments"
          emptyIcon={<PiBuildingsLight />}
          onItemRestored={() => dispatch(fetchAllDepartments() as any).then((result: any) => { if (Array.isArray(result)) setDepartments(result); })}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      /* Measured, not auto: the table below fills this box instead of
         sizing to its own rows under a viewport-guess cap. */
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div data-tour-id="dept-list-content" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none" />
        {error && (
          <div className="mb-6 p-4 mx-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 text-danger-600 dark:text-danger-400 rounded-lg">
            {error}
          </div>
        )}


        {viewMode === 'grid' ? (
          <div className="px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 min-h-0 overflow-y-auto custom-scrollbar-main pb-4">
              {isFetching && departments.length === 0 ? (
                <Skeleton variant="grid" rows={3} />
              ) : displayedDepartments.length > 0 ? (
                displayedDepartments.map((dept) => (
                  <DepartmentGridItem
                    key={dept.id}
                    department={{
                      id: dept.id,
                      name: dept.name,
                      description: dept.description,
                      is_active: dept.is_active,
                      members: dept.members,
                      document_count: dept.document_count,
                      feed_item_count: dept.feed_item_count,
                    }}
                    onClick={(id) => history.push(`/departments/${id}`)}
                  />
                ))
              ) : null}
            </div>
            {hasMore && displayedDepartments.length > 0 && (
              <div className="flex justify-center py-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="small"
                  onClick={loadMore}
                >
                  {t('Load More')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* The table fills the measured box above rather than sizing to its
             rows — one scroller, and it reaches the bottom of the window. */
          <div className="flex-1 min-h-0 flex flex-col">
            {isFetching && departments.length === 0 ? (
              <Skeleton variant="table" rows={2} />
            ) : (
              <Table
                columns={columns}
                data={data}
                onRowClick={(row) => {
                  const dept = displayedDepartments.find((d) => d.id === row.id);
                  if (dept) {
                    history.push(`/departments/${dept.id}`);
                  }
                }}
                actions={departmentTableActions}
                hasMore={hasMore}
                isLoadingMore={false}
                onLoadMore={loadMore}
                total_items={filteredDepartments.length}
                total_showing_items={displayedDepartments.length}
                setIsShowing={true}
                showingLabel={t('departments')}
                resizableColumns={true}
                defaultColumnWidths={{
                  'Name': 200,
                  'Practice Areas': 200,
                  'Members': 120,
                  'Documents': 110,
                  'Feed': 100,
                  'Description': 240,
                  'Status': 120,
                  'Actions': 60,
                }}
                emptyState={{
                  icon: <PiBuildingsLight />,
                  headline: searchTerm ? t('No results found.') : t('You have no departments added yet.'),
                  subtext: searchTerm
                    ? t('Try adjusting your search to find what you\'re looking for.')
                    : t('Start by creating a department to get organized.'),
                  button: {
                    label: searchTerm ? t('Clear Search') : t('Create Department'),
                    onClick: searchTerm ? () => setSearchTerm('') : () => history.push('/departments?add=true'),
                  },
                }}
              />
            )}
          </div>
        )}
      </div>
      )}

      <FeatureTour
        steps={departmentListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />

      {/* Audit Log Side Panel with Backdrop */}
      {showAuditLog && auditTarget && (
        <ActivityDrawer onClose={() => setShowAuditLog(false)}>
          <AuditLogPanel
            entityType="Department"
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => {
              setShowAuditLog(false);
              setAuditTarget(null);
            }}
          />
        </ActivityDrawer>
      )}
    </>
  );
};

export default DepartmentList;
